import { FormControl, Grid, Radio, Typography } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import classnames from 'classnames'
import React, { useCallback, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useSubmitFormOnDirty from '../../../../hooks/useSubmitFormOnDirty'
import { OrderType } from '../../../../lib/cart/item'
import { autoShipIntervalOptions } from '../../../../lib/cart/item-control-options'
import { CartItem } from '../../../../lib/cart/types'
import { useShowAutoShipAndSaveToUserGroup } from '../../../../lib/m2/user-groups'
import { useTestKitProducts } from '../../../../lib/products/hooks/useTestKitProducts'
import UpdateCartItemErrorAlert from '../../../cart/UpdateCartItemErrorAlert'
import { AutoShipControlLabel } from '../../../global/cart/AutoShipControlLabel'
import * as Styled from './styled'

export interface ItemOrderTypeProps {
  disabled: boolean
  item: CartItem

  updateCartItem(props: {
    orderType?: number
    autoShipInterval?: number
  }): Promise<{ error?: Error }>
}

interface FormValues {
  orderType: OrderType
  autoShipInterval?: number
}

const ItemOrderType: React.FC<ItemOrderTypeProps> = ({ disabled, item, updateCartItem }) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const showAutoShipAndSaveToUserGroup = useShowAutoShipAndSaveToUserGroup()
  const showAutoShipAndSave = !!featureFlags?.autoShipAndSave && showAutoShipAndSaveToUserGroup
  const defaultOrderType = useMemo(() => {
    const itemOrderType = item.item_attributes?.dfh_order_type
    switch (itemOrderType) {
      case OrderType.SingleOrder:
        return OrderType.SingleOrder
      case OrderType.AutoShip:
        return OrderType.AutoShip
      default:
        return OrderType.SingleOrder
    }
  }, [item.item_attributes?.dfh_order_type])

  const defaultAutoShipIntervalOption = useMemo(() => {
    const itemAutoShipInterval = item.item_attributes?.dfh_autoship_interval?.toString() || '1'
    return autoShipIntervalOptions.find(({ value }) => value === itemAutoShipInterval)
  }, [item.item_attributes?.dfh_autoship_interval])

  const defaultAutoShipInterval = defaultAutoShipIntervalOption
    ? Number(defaultAutoShipIntervalOption.value)
    : undefined

  const { control, errors, formState, handleSubmit, reset, setError, watch } = useForm<FormValues>({
    mode: 'onChange',
  })

  const currentOrderType = watch('orderType', defaultOrderType)

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async ({ autoShipInterval, orderType }) => {
      if (Number.isNaN(orderType)) {
        setError('orderType', { message: 'Numeric value required', type: 'validate' })
        return
      }

      const itemOrderType = item.item_attributes?.dfh_order_type
      const itemAutoShipInterval = item.item_attributes?.dfh_autoship_interval

      // avoid update orderType and autoShipInterval match cart item attributes
      if (
        orderType === itemOrderType &&
        (orderType === OrderType.SingleOrder ||
          (orderType === OrderType.AutoShip && autoShipInterval === itemAutoShipInterval))
      ) {
        return
      }

      const result = await updateCartItem({
        orderType,
        ...(orderType === OrderType.AutoShip && {
          autoShipInterval,
        }),
      })

      if (result.error) {
        setError('orderType', { message: result.error.message, type: 'server' })
      }
    },
    [
      item.item_attributes?.dfh_autoship_interval,
      item.item_attributes?.dfh_order_type,
      setError,
      updateCartItem,
    ],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        orderType: defaultOrderType,
        ...(defaultOrderType === OrderType.AutoShip && {
          autoShipInterval: Number(defaultAutoShipIntervalOption?.value),
        }),
      }),
      [defaultAutoShipIntervalOption?.value, defaultOrderType],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
  })

  const { isTestKitSku } = useTestKitProducts()
  const showAutoShip = !isTestKitSku(item.product.sku || '')

  if (!showAutoShip) {
    return null
  }
  return (
    <>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <Controller
            control={control}
            defaultValue={defaultOrderType}
            name="orderType"
            render={({ onBlur, onChange, value }) => (
              <Styled.RadioGroup
                row
                value={value}
                onBlur={onBlur}
                onChange={(e) => onChange(Number(e.target.value))}
              >
                <Styled.FormControlLabel
                  disabled={disabled}
                  className={classnames({ selected: value === OrderType.SingleOrder })}
                  control={<Radio checkedIcon={<CheckCircleIcon />} />}
                  label="Single"
                  value={OrderType.SingleOrder}
                />
                <Styled.FormControlLabel
                  disabled={disabled}
                  className={classnames({ selected: value === OrderType.AutoShip })}
                  control={<Radio checkedIcon={<CheckCircleIcon />} />}
                  label={
                    <AutoShipControlLabel showSaveText={showAutoShipAndSave} disabled={disabled} />
                  }
                  style={
                    showAutoShipAndSave
                      ? {
                          width: 'fit-content',
                          paddingRight: '6px',
                        }
                      : undefined
                  }
                  value={OrderType.AutoShip}
                />
              </Styled.RadioGroup>
            )}
            rules={{
              validate: (value) => {
                switch (Number(value)) {
                  case OrderType.SingleOrder:
                  case OrderType.AutoShip:
                    return true
                  default:
                    return 'Must select an option'
                }
              },
              valueAsNumber: true,
            }}
          />
        </FormControl>
      </Grid>
      {errors.orderType && errors.orderType.type !== 'server' && (
        <Grid item xs={12}>
          <Styled.ErrorMessage variant="body2">{errors.orderType.message}</Styled.ErrorMessage>
        </Grid>
      )}
      <Grid item xs={12}>
        {currentOrderType !== OrderType.AutoShip ? (
          <Typography variant="caption" paragraph display="block">
            {showAutoShipAndSave
              ? 'Set up Auto-Ship & Save 5% on this product. Your Auto-Ship can be canceled at any time.'
              : 'Set up Auto-Ship and never run out of the supplements you love! Cancel at any time.'}
          </Typography>
        ) : (
          <>
            {showAutoShipAndSave && (
              <Typography variant="caption" paragraph display="block">
                You will receive a 5% discount on this product.
              </Typography>
            )}
            <Controller
              control={control}
              defaultValue={defaultAutoShipInterval}
              name="autoShipInterval"
              render={({ onBlur, onChange, name, ref, value }) => (
                <Styled.TextField
                  disabled={disabled}
                  inputRef={ref}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  SelectProps={{
                    native: true,
                    startAdornment: (
                      <Styled.InputAdornment disableTypography position="start">
                        Ship every:
                      </Styled.InputAdornment>
                    ),
                  }}
                  select
                  value={value}
                  variant="outlined"
                >
                  {autoShipIntervalOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Styled.TextField>
              )}
              rules={{
                validate: (value) => Number(value) >= 1 || 'Must be greater than or equal to 1',
                valueAsNumber: true,
              }}
            />
          </>
        )}
      </Grid>
      {errors.autoShipInterval && (
        <Grid item xs={12}>
          <Styled.ErrorMessage variant="body2">
            {errors.autoShipInterval.message}
          </Styled.ErrorMessage>
        </Grid>
      )}
      {errors.orderType && errors.orderType.type === 'server' && (
        <Grid item xs={12}>
          <UpdateCartItemErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)} />
        </Grid>
      )}
    </>
  )
}

export default ItemOrderType
